import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const ENERGY_DIARY_STARTED_KEY = 'mingpan_energy_diary_started_at';

// ============ 测试阶段，免注册使用 ============
const TEST_MODE = false;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async (authToken) => {
    if (!authToken) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (data.success) {
        setUser(data.data);
      } else {
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      }
    } catch {
      // 网络瞬断时保留当前用户状态，不清除登录态
      setLoading(false);
      return;
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUser(token);
  }, [token, fetchUser]);

  const register = async (username, email, password, inviteCode) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password, invite_code: inviteCode })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('auth_token', data.data.token);
        setToken(data.data.token);
        setUser(data.data.user);
      }
      return { success: data.success, message: data.message };
    } catch (e) {
      return { success: false, message: '网络连接失败，请检查网络后重试' };
    }
  };

  const login = async (email, password) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('auth_token', data.data.token);
        setToken(data.data.token);
        setUser(data.data.user);
      }
      return { success: data.success, message: data.message };
    } catch (e) {
      return { success: false, message: '网络连接失败，请检查网络后重试' };
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) await fetchUser(token);
  };

  const checkAccess = async (featureId) => {
    if (TEST_MODE) return { allowed: true };
    if (!token) return { allowed: false, message: '请先登录' };
    const res = await fetch(`${API_BASE}/api/check-access/${featureId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    return data.data || { allowed: false, message: '检查失败' };
  };

  const consumeFeature = async (featureId) => {
    if (TEST_MODE) return true;
    if (!token) return false;
    try {
      const res = await fetch(`${API_BASE}/api/consume-feature/${featureId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        await refreshUser();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const createOrder = async (itemId, itemType, paymentMethod = 'alipay') => {
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE}/api/payment/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ item_id: itemId, item_type: itemType, payment_method: paymentMethod })
      });
      return await res.json();
    } catch {
      return null;
    }
  };

  const confirmPayment = async (orderId) => {
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE}/api/payment/confirm/${orderId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        await refreshUser();
      }
      return data;
    } catch {
      return null;
    }
  };

  const checkPaymentStatus = async (orderId) => {
    if (!token) return { success: false };
    try {
      const res = await fetch(`${API_BASE}/api/payment/confirm/${orderId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        await refreshUser();
      }
      return data;
    } catch {
      return { success: false };
    }
  };

  const saveProfile = async (baziData, profileName = '默认档案') => {
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE}/api/auth/save-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ bazi_data: baziData, profile_name: profileName })
      });
      const data = await res.json();
      if (data.success) {
        await refreshUser();
      }
      return data;
    } catch {
      return null;
    }
  };

  // ============ 新增：板块解锁 ============
  const unlockBoard = async (featureId) => {
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE}/api/unlock-board/${featureId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        await refreshUser();
      }
      return data;
    } catch {
      return null;
    }
  };

  // ============ 会员信息推导（v2.0 对话次数模型） ============
  const membershipType = TEST_MODE ? 'quarterly' : (user?.membership_type || user?.membership_tier || 'free');
  // 旧 single 用户视为 free（但保留兼容）
  const effectiveType = membershipType === 'single' ? 'free' : membershipType;
  const isPaidMember = TEST_MODE || (effectiveType !== 'free');
  const isMember = TEST_MODE || effectiveType === 'quarterly' || effectiveType === 'year';
  const isSingle = TEST_MODE ? false : membershipType === 'single';  // 旧用户兼容
  const isFree = TEST_MODE ? false : (effectiveType === 'free');

  // 会员到期信息（兼容后端 sanitize_user 的 type 和 get_membership_status 的 tier 两种字段名）
  const _rawMembership = user?.membership;
  const _mType = _rawMembership?.type || _rawMembership?.tier || membershipType;
  const membershipInfo = _rawMembership ? {
    type: _mType,
    expires_at: _rawMembership?.expires_at || _rawMembership?.expiry || user?.membership_expiry || null,
    days_left: _rawMembership?.days_left ?? null,
  } : {
    type: membershipType,
    expires_at: user?.membership_expiry || null,
    days_left: null,
  };

  // 单次解锁信息
  const singleUnlocked = user?.single_unlocked || null;
  const singleUnlockTokens = user?.single_unlock_tokens || 0;

  // ============ 知知先生对话状态（会话+次数模型） ============
  // TEST_MODE: 访客模式提供 1 次对话（3轮）体验
  const guestZhiStatus = TEST_MODE ? {
    can_chat: true,
    remaining_sessions: 1,
    remaining_rounds: 3,
    max_rounds_per_session: 3,
    max_sessions_per_month: 1,
    guest: true,
  } : null;

  const zhiStatus = user?.zhi_status ?? guestZhiStatus;

  const getZhiChatRemaining = () => {
    // v2.0: 返回总剩余对话次数
    if (!zhiStatus) return 0;
    return zhiStatus.total_remaining ?? 0;
  };

  const canChat = () => {
    if (!zhiStatus) return false;
    return zhiStatus.can_chat === true;
  };

  const getZhiRoundInfo = () => {
    // v2.0: 返回次数信息（兼容旧接口名）
    if (!zhiStatus) return { remaining: 0, max: 0 };
    return {
      remaining: zhiStatus.total_remaining ?? 0,
      max: 999,
    };
  };

  const incrementZhiChat = async (messageHash = null) => {
    try {
      const body = messageHash ? JSON.stringify({ message_hash: messageHash }) : '{}';
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const res = await fetch(`${API_BASE}/api/zhi/consume`, {
        method: 'POST',
        headers,
        body,
      });
      const data = await res.json();
      if (data.success) {
        await refreshUser();
      }
    } catch {
      // 静默失败，不影响聊天体验
    }
  };

  // ============ 对话包购买 ============
  const buyChatPack = async (packId) => {
    // 通过创建订单 + 确认支付的流程（与会员购买一致）
    // 此函数不再单独调用 /api/zhi/consume
    // 实际购买通过 MembershipPage 的 createOrder + confirmPayment 完成
  };

  // ============ 能量日记：追踪首次使用时间 ============
  const getEnergyDiaryStartedAt = () => {
    // 优先从后端获取
    if (user?.energy_diary_started_at) return user.energy_diary_started_at;
    // 回退localStorage
    try {
      const saved = localStorage.getItem(ENERGY_DIARY_STARTED_KEY);
      if (saved) return saved;
    } catch {}
    return null;
  };

  const setEnergyDiaryStartedAt = async () => {
    const already = getEnergyDiaryStartedAt();
    if (already) return already;
    
    const now = new Date().toISOString();
    localStorage.setItem(ENERGY_DIARY_STARTED_KEY, now);
    
    // 同步到后端
    if (token) {
      try {
        await fetch(`${API_BASE}/api/energy-diary/start`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        await refreshUser();
      } catch {}
    }
    return now;
  };

  const getEnergyDiaryDaysUsed = () => {
    const started = getEnergyDiaryStartedAt();
    if (!started) return 0;
    const start = new Date(started);
    const now = new Date();
    return Math.ceil((now - start) / (1000 * 60 * 60 * 24));
  };

  const canUseEnergyDiary = () => {
    if (isMember) return true;
    const days = getEnergyDiaryDaysUsed();
    return days <= 30;
  };

  const isAdmin = user?.is_admin === true;

  const value = {
    // 基础认证
    user, token, loading,
    login, register, logout, refreshUser,
    isAdmin,

    // 权限检查
    checkAccess, consumeFeature,

    // 支付
    createOrder, confirmPayment, checkPaymentStatus,

    // 新：板块解锁
    unlockBoard,

    // 档案
    saveProfile,

    // 会员信息
    membershipType,      // free | single | quarterly | year
    isPaidMember,        // 是否付费（非free）
    isMember,            // 是否会员（quarterly/year）
    isSingle,            // 是否单次用户
    isFree,              // 是否免费用户
    membershipInfo,      // { type, expires_at, days_left }
    singleUnlocked,      // 已解锁的板块
    singleUnlockTokens,  // 剩余解锁次数

    // 知知先生对话状态（v2.0 对话次数模型）
    zhiStatus,
    lifetimeZhiChatsRemaining: zhiStatus?.total_remaining ?? null,  // 兼容旧代码
    getZhiChatRemaining,
    getZhiRoundInfo,
    canChat,
    incrementZhiChat,
    buyChatPack,

    // 能量日记
    getEnergyDiaryStartedAt,
    setEnergyDiaryStartedAt,
    getEnergyDiaryDaysUsed,
    canUseEnergyDiary,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
